/* eslint-disable react/jsx-no-duplicate-props */
import {
    DialogContent, InputAdornment, TextField
} from '@mui/material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
    CustomIcon, CustomTooltip, Datepicker, FlexContainer, FontIcon, FormLine, Question, RequiredSelectHack, SkeletonForm, Text
} from 'components';
import { useStores } from 'hooks';
import { ModalProps } from 'hooks/ModalProvider';
import { BottleView } from 'models';
import React, {
 Fragment, useEffect, useState, useMemo
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { FluidsService } from 'services';
import shortid from 'shortid';
import { BOTTLE_TYPES, STOCK_ROLES_WITH_INTERVENTION_MANAGER } from 'utils/constants';
import {
    BottleHelper,
    FormHelper,
    UserHelper,
    formatDate,
    translate
} from 'utils/helpers';
import { ModalHeader } from './ModalHeader';
import { CustomActionButtons } from './_CustomActionButtons';

interface ExtendedBottleView extends BottleView {
    [key: string]: any;
    receptionDate?: string;
    stringType?: 'NEW_BOTTLE' | 'RETRIEVAL_BOTTLE' | 'TRANSFER_BOTTLE';
}

export const CreateBottleModal = ({
                                      bottleId,
                                      isInit = false, onSubmit,
                                      onClose
                                  }: ModalProps<BottleView> & {
    bottleId: string,
    isInit?: boolean
}) => {
    const { fluidStore } = useStores();
    const [isLoading, setIsLoading] = useState(false);
    const [originalApiData, setOriginalApiData] = useState<any>(null);

    const {
        control,
        setValue,
        handleSubmit,
        watch,
        register,
        formState: { errors }
    } = useForm<ExtendedBottleView>({
        defaultValues: new BottleView() as ExtendedBottleView
    });
    const stringType = watch('stringType');
    const fluid = watch('fluid');
    const canDelete = watch('canDelete');
    const measuredAmount = watch('measuredAmount');
    const validated = watch('validated');

    const formattedfluidOptionsExtended = useMemo(() => fluidStore.fluidExtendedOptions.map(fluid => ({
        value: fluid.id,
        label: fluid.name
    })), [fluidStore.fluidExtendedOptions]);

    const canValidate = bottleId && !validated && UserHelper.hasAccessRight(STOCK_ROLES_WITH_INTERVENTION_MANAGER);

    useEffect(() => {
        register('identifier', { required: true, pattern: /.*\S.*/ });
        register('stringType');
        register('fluid');
        register('receptionDate');
        register('measuredAmount');
        register('initialAmount');
        register('maximumCapacity');
        register('volume');
        register('isNewFluid');
        register('boughtInOperatorCountry');
        register('canDelete');
        register('validated');
    }, [register]);

    useEffect(() => {
        if (!bottleId) {
            if (!isInit) {
                setValue('stringType', 'RETRIEVAL_BOTTLE');
                setValue('receptionDate', formatDate(new Date()));
            }
            return;
        }
        setIsLoading(true);

        FluidsService.getBottle(bottleId)
            .then((resp) => {
                setOriginalApiData(resp);
                console.log('API Response:', resp);

                if (resp.id) setValue('id', resp.id);
                if (resp.identifier) setValue('identifier', resp.identifier);
                if (resp.initialAmount) setValue('initialAmount', resp.initialAmount);
                if (resp.measuredAmount) setValue('measuredAmount', resp.measuredAmount);
                if (resp.maximumCapacity) setValue('maximumCapacity', resp.maximumCapacity);
                if (resp.volume) setValue('volume', resp.volume);
                if (resp.canDelete !== undefined) setValue('canDelete', resp.canDelete);
                if (resp.validated !== undefined) setValue('validated', resp.validated);
                if (resp.isNewFluid !== undefined) setValue('isNewFluid', resp.isNewFluid);
                if (resp.boughtInOperatorCountry !== undefined) setValue('boughtInOperatorCountry', resp.boughtInOperatorCountry);
                const selectedFluid = formattedfluidOptionsExtended.find(f => `${f.value}` === resp.fluid?.value);
                if (selectedFluid) setValue('fluid', selectedFluid);
                if (resp.bottleType?.value) setValue('stringType', resp.bottleType.value as 'NEW_BOTTLE' | 'RETRIEVAL_BOTTLE' | 'TRANSFER_BOTTLE');
            })
            .finally(() => setIsLoading(false));
    }, [bottleId, isInit, setValue, formattedfluidOptionsExtended]);

    const handleValidateForm = (data: ExtendedBottleView) => {
        const formDataWithoutUndefined = Object.entries(data).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, any>);
        const combinedData = bottleId
            ? { ...originalApiData, ...formDataWithoutUndefined }
            : data;

        const submissionData = {
            ...combinedData,
            bottleType: {
                value: combinedData.stringType,
                label: combinedData.stringType
            },
            initialAmount: combinedData.initialAmount != null ? Number(combinedData.initialAmount) : combinedData.initialAmount,
            measuredAmount: combinedData.measuredAmount != null ? Number(combinedData.measuredAmount) : combinedData.measuredAmount,
            maximumCapacity: combinedData.maximumCapacity != null ? Number(combinedData.maximumCapacity) : combinedData.maximumCapacity,
            theoricalAmount: combinedData.theoricalAmount != null ? Number(combinedData.theoricalAmount) : combinedData.theoricalAmount,
            volume: combinedData.volume != null ? Number(combinedData.volume) : combinedData.volume,
            fluid: FormHelper.SELECT_ADAPTOR_TO_BACK(combinedData.fluid)
        };
        onSubmit(submissionData);
    };
    const displayHeader = () => {
        if (bottleId) {
            return translate('modalBottle.editBottle');
        }
        if (isInit) {
            return translate('modalBottle.registerBottle');
        }
        return translate('modalBottle.newBottle');
    };
    const bottleTypeButtons = useMemo(() => BOTTLE_TYPES.map((type) => {
        if (!bottleId && !isInit && type.key === 'NEW_BOTTLE') {
            return null;
        }
        return (
          <ToggleButton
                aria-label={BottleHelper.getTranslatedType({ type: type.key })}
                disabled={bottleId && !canDelete}
                disableRipple
                key={type.key}
                value={type.key}
            >
            {BottleHelper.getTranslatedType({ type: type.key })}
          </ToggleButton>
        );
    }), [bottleId, isInit, canDelete]);

    return (
      <form autoComplete="off" onSubmit={handleSubmit(handleValidateForm)}>
        <ModalHeader onClose={onClose}>
          <CustomIcon icon={<FontIcon fontSize="2.4rem" icon="icon-container" marginLeft="-0.6rem" />} marginRight="1rem" />
          {displayHeader()}
        </ModalHeader>

        {isLoading ? (
          <SkeletonForm />
            ) : (
              <>
                <DialogContent style={{ paddingTop: '10px' }}>
                  <FlexContainer flexDirection="column">
                    <Question label={translate('modalBottle.bottleType')}>
                      <Controller
                                    control={control}
                                    name="stringType"
                                    render={({ field }) => (
                                      <ToggleButtonGroup
                                            aria-label={translate('modalBottle.bottleType')}
                                            data-cy="selectBottleType"
                                            exclusive
                                            size="medium"
                                            value={field.value || null}
                                            onChange={(event, newValue) => {
                                                if (newValue !== null) {
                                                    field.onChange(newValue);
                                                }
                                            }}
                                        >
                                        {bottleTypeButtons}
                                      </ToggleButtonGroup>
                                    )}
                                />
                    </Question>

                    <FormLine>
                      <Controller
                                    control={control}
                                    name="identifier"
                                    render={({ field }) => (
                                      <TextField
                                            {...field}
                                            error={!!errors.identifier}
                                            inputProps={{ pattern: '.*\\S.*' }}
                                            label={translate('common.identifier')}
                                            name="identifier"
                                            required
                                        />
                                    )}
                                />
                      <CustomTooltip text={translate('warnings.bottle.identifierMaxLength')} />
                    </FormLine>

                    {!bottleId && (
                    <Controller
                                    control={control}
                                    name="receptionDate"
                                    render={({ field: { value, onChange } }) => (
                                      <Datepicker
                                            dataCy="createBottleModalReceptionDate"
                                            disableFuture
                                            label="common.receptionDate"
                                            name="receptionDate"
                                            style={{ marginTop: '.5rem' }}
                                            value={value}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            )}

                    <Question label={`${translate('modalBottle.chooseFluid')} :`}>
                      <FormLine style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <Controller
                                        control={control}
                                        name="fluid"
                                        render={({ field }) => (
                                          <Select
                                                {...field}
                                                id="selectFluid"
                                                isClearable
                                                isDisabled={bottleId && !canDelete}
                                                isSearchable
                                                menuPlacement={isMobileOnly ? 'top' : 'bottom'}
                                                menuPortalTarget={document.body}
                                                noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                                options={fluidStore.fluidOptions}
                                                placeholder={translate('common.searchByFluidType')}
                                                required={stringType === 'NEW_BOTTLE'}
                                                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                            />
                                        )}
                                    />
                        {stringType !== 'NEW_BOTTLE' && (
                        <CustomTooltip text={translate('modalBottle.fluidNotMandatory')} />
                                    )}
                      </FormLine>
                      {stringType === 'NEW_BOTTLE' && (
                        <RequiredSelectHack value={fluid || ''} />
                                )}
                    </Question>

                    {stringType === 'NEW_BOTTLE' && (
                    <Fragment>
                      <Controller
                                        control={control}
                                        name="isNewFluid"
                                        render={({ field }) => (
                                          <ToggleButtonGroup
                                                aria-label={translate('common.fluidType')}
                                                data-cy="isNewFluid"
                                                exclusive
                                                size="small"
                                                value={field.value}
                                                onChange={(event, value) => {
                                                    if (value !== null) {
                                                        field.onChange(value);
                                                    }
                                                }}
                                            >
                                            <ToggleButton
                                                    aria-label={translate('fluids.newFluid')}
                                                    data-cy="newFluid"
                                                    disabled={bottleId && !canDelete}
                                                    disableRipple
                                                    key="new-fluid-true"
                                                    value
                                                >
                                              {translate('fluids.newFluid')}
                                            </ToggleButton>
                                            <ToggleButton
                                                    aria-label={translate('fluids.regeneratedFluid')}
                                                    data-cy="regeneratedFluid"
                                                    disabled={bottleId && !canDelete}
                                                    disableRipple
                                                    key="new-fluid-false"
                                                    value={false}
                                                >
                                              {translate('fluids.regeneratedFluid')}
                                            </ToggleButton>
                                          </ToggleButtonGroup>
                                        )}
                                    />
                      <FlexContainer alignItems="center">
                        <Text margin="0 2rem 0 0">{translate('fluids.boughtInOperatorCountry')}</Text>
                        <Controller
                                            control={control}
                                            name="boughtInOperatorCountry"
                                            render={({ field }) => (
                                              <ToggleButtonGroup
                                                    aria-label={translate('fluids.boughtInOperatorCountry')}
                                                    data-cy="boughtInOperatorCountry"
                                                    exclusive
                                                    size="small"
                                                    style={{ marginTop: '1rem' }}
                                                    value={field.value}
                                                    onChange={(event, value) => {
                                                        if (value !== null) {
                                                            field.onChange(value);
                                                        }
                                                    }}
                                                >
                                                <ToggleButton
                                                        aria-label={translate('common.yes')}
                                                        data-cy="yes"
                                                        disabled={bottleId && !canDelete}
                                                        disableRipple
                                                        key="bought-country-true"
                                                        value
                                                    >
                                                  {translate('common.yes')}
                                                </ToggleButton>
                                                <ToggleButton
                                                        aria-label={translate('common.no')}
                                                        data-cy="no"
                                                        disabled={bottleId && !canDelete}
                                                        disableRipple
                                                        key="bought-country-false"
                                                        value={false}
                                                    >
                                                  {translate('common.no')}
                                                </ToggleButton>
                                              </ToggleButtonGroup>
                                            )}
                                        />
                      </FlexContainer>
                    </Fragment>
                            )}

                    {fluid && (
                    <Fragment>
                      {stringType === 'NEW_BOTTLE' && !bottleId && (
                      <Controller
                                            control={control}
                                            name="initialAmount"
                                            render={({ field }) => (
                                              <TextField
                                                    {...field}
                                                    disabled={!!bottleId}
                                                    error={!!errors.initialAmount}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">{translate('common.weightUnit')}</InputAdornment>
                                                    }}
                                                    inputProps={{
                                                        step: '0.01',
                                                        min: 0
                                                    }}
                                                    label={translate('common.initialQuantity')}
                                                    name="initialAmount"
                                                    style={{ marginTop: '.5rem' }}
                                                    type="number"
                                                />
                                            )}
                                        />
                                    )}

                      {!bottleId && (
                      <Controller
                                            control={control}
                                            name="measuredAmount"
                                            render={({ field }) => (
                                              <TextField
                                                    {...field}
                                                    disabled={bottleId && !canDelete}
                                                    error={!!errors.measuredAmount}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">{translate('common.weightUnit')}</InputAdornment>
                                                    }}
                                                    inputProps={{
                                                        step: '0.01',
                                                        min: 0
                                                    }}
                                                    label={translate('common.measuredQuantityNow')}
                                                    name="measuredAmount"
                                                    required
                                                    style={{ marginTop: '.5rem' }}
                                                    type="number"
                                                />
                                            )}
                                        />
                                    )}
                    </Fragment>
                            )}

                    <Controller
                                control={control}
                                name="maximumCapacity"
                                render={({ field }) => (
                                  <TextField
                                        {...field}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">{translate('common.weightUnit')}</InputAdornment>
                                        }}
                                        inputProps={{
                                            step: '0.01',
                                            min: measuredAmount || 1
                                        }}
                                        label={translate('common.maximumCapacity')}
                                        name="maximumCapacity"
                                        style={{ marginTop: '.5rem' }}
                                        type="number"
                                    />
                                )}
                            />

                    <Controller
                                control={control}
                                name="volume"
                                render={({ field }) => (
                                  <TextField
                                        {...field}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">L</InputAdornment>
                                        }}
                                        inputProps={{
                                            step: '0.01'
                                        }}
                                        label={translate('common.bottleVolume')}
                                        name="volume"
                                        style={{ marginTop: '.5rem' }}
                                        type="number"
                                    />
                                )}
                            />
                  </FlexContainer>
                </DialogContent>

                <CustomActionButtons
                        forValidation={canValidate}
                        isLoading={isLoading}
                        onCancel={onClose}
                        onClose={onClose}
                    />
              </>
            )}
      </form>
    );
};
